import { ContentCard } from '@braze/react-native-sdk';
import { PayloadAction } from '@reduxjs/toolkit';

import { dedupeByTitleAndDescription, parseWebContentCards } from 'state/braze/hooks/utils';
import { CONTENT_CARD_FEED, ContentCardTypes, WebContentCard } from 'state/braze/types';
import { createAppSlice } from 'state/global-state/utils';

import { IContentCardsState, LocalReadsSyncStatus } from './content-cards.types';

export const initialState: IContentCardsState = {
  contentCards: [],
  newsFeedContentCards: [],
  homePageFeedContentCards: [],
  webContentCards: [],
  contentCardsLoaded: false,
  sessionOwnerId: null,
  serverReadNotificationIds: [],
  serverReadStatus: 'pending',
  resolvedNotificationIds: [],
  localReadsSyncStatus: 'idle',
  localReadsVersion: 0,
  sessionId: 0,
};

const sortContentCards = (cards: ContentCard[]): ContentCard[] => {
  const { cardsWithOrder, cardsWithNoOrder } = cards.reduce(
    (acc, card) => {
      const order = parseFloat(card.extras?.order);
      if (isNaN(order)) {
        acc.cardsWithNoOrder.push(card);
      } else {
        acc.cardsWithOrder.push(card);
      }
      return acc;
    },
    { cardsWithOrder: [], cardsWithNoOrder: [] } as {
      cardsWithOrder: ContentCard[];
      cardsWithNoOrder: ContentCard[];
    }
  );

  cardsWithOrder.sort((a, b) => parseFloat(a.extras.order) - parseFloat(b.extras.order));

  return [...cardsWithOrder, ...cardsWithNoOrder];
};

const updateContentCardsState = (
  state: IContentCardsState,
  payload: ContentCard[],
  { fromCache }: { fromCache: boolean }
) => {
  const contentCards = dedupeByTitleAndDescription(payload);
  // Both feeds only surface Captioned cards; Classic / ImageOnly are dropped here so
  // homepage and news stay aligned without per-consumer type filtering. Control cards are kept
  // even though they carry no display type: they are never rendered, but their impression still
  // has to be logged for the A/B comparison against the treatment arm.
  const feedContentCards = contentCards.filter(
    card => card.isControl || card.type === ContentCardTypes.CAPTIONED
  );

  state.contentCards = contentCards;
  state.newsFeedContentCards = sortContentCards(
    feedContentCards.filter(({ extras }) => extras.feed === CONTENT_CARD_FEED.NEWS)
  );
  state.homePageFeedContentCards = sortContentCards(
    feedContentCards.filter(({ extras }) => extras.feed === CONTENT_CARD_FEED.HOME_PAGE)
  );
  // Cache-sourced payloads still update the cards (for display) but must not flip
  // contentCardsLoaded - Braze's own SDK cache can be stale (see contentCardsLoaded's doc).
  if (!fromCache) {
    state.contentCardsLoaded = true;
  }
};

export const contentCardsSlice = createAppSlice({
  name: 'content-cards',
  initialState,
  reducers: {
    setWebContentCards: (
      state,
      { payload }: PayloadAction<{ cards: WebContentCard[]; fromCache: boolean }>
    ) => {
      state.webContentCards = payload.cards;

      const parsedContentCards = parseWebContentCards(payload.cards);
      updateContentCardsState(state, parsedContentCards, { fromCache: payload.fromCache });
    },
    setContentCards: (
      state,
      { payload }: PayloadAction<{ cards: ContentCard[]; fromCache: boolean }>
    ) => {
      updateContentCardsState(state, payload.cards, { fromCache: payload.fromCache });
    },
    // Local storage isn't reactive; bumping this version signals the derived unread count to re-read it.
    localReadContentCardsChanged: state => {
      state.localReadsVersion += 1;
    },
    // Resets session-scoped state on a genuine identity change. Compares against Redux state
    // (not a component ref) so it survives the calling hook's own remount mid-transition.
    resetNotificationCenterSession: (
      state,
      { payload: identity }: PayloadAction<string | null>
    ) => {
      if (state.sessionOwnerId === identity) {
        return;
      }
      state.sessionOwnerId = identity;
      state.localReadsVersion += 1;
      state.serverReadNotificationIds = [];
      state.serverReadStatus = 'pending';
      state.resolvedNotificationIds = [];
      state.localReadsSyncStatus = 'idle';
      state.contentCardsLoaded = false;
      // Bumped unconditionally so an in-flight request from the previous owner (which this alone
      // can't detect from the other fields looking "clean") gets invalidated.
      state.sessionId += 1;
    },
    // Adds mutation-confirmed ids without touching serverReadStatus - only applyServerReadStatus
    // (the read-status query) may mark it resolved. No-ops (same reference) when nothing's new.
    mergeServerReadNotificationIds: (
      state,
      { payload }: PayloadAction<{ ids: string[]; sessionId: number }>
    ) => {
      // Ignore a response from a session that has since ended (logout/relogin).
      if (payload.sessionId !== state.sessionId) {
        return;
      }

      const hasNewIds = payload.ids.some(id => !state.serverReadNotificationIds.includes(id));
      if (!hasNewIds) {
        return;
      }

      state.serverReadNotificationIds = [
        ...new Set([...state.serverReadNotificationIds, ...payload.ids]),
      ];
    },
    // The read-status query's result: merges ids like above AND marks resolved (the only reducer
    // allowed to). Not skipped when nothing's new, since the first resolved-empty response must
    // still flip the status. `requestedIds` (the full set asked about, not just the read ones)
    // is unioned into resolvedNotificationIds - see that field's doc.
    applyServerReadStatus: (
      state,
      { payload }: PayloadAction<{ ids: string[]; requestedIds: string[]; sessionId: number }>
    ) => {
      if (payload.sessionId !== state.sessionId) {
        return;
      }

      const hasNewReadIds = payload.ids.some(id => !state.serverReadNotificationIds.includes(id));
      const hasNewResolvedIds = payload.requestedIds.some(
        id => !state.resolvedNotificationIds.includes(id)
      );
      if (!hasNewReadIds && !hasNewResolvedIds && state.serverReadStatus === 'resolved') {
        return;
      }

      state.serverReadNotificationIds = [
        ...new Set([...state.serverReadNotificationIds, ...payload.ids]),
      ];
      state.resolvedNotificationIds = [
        ...new Set([...state.resolvedNotificationIds, ...payload.requestedIds]),
      ];
      state.serverReadStatus = 'resolved';
    },
    setLocalReadsSyncStatus: (
      state,
      { payload }: PayloadAction<{ status: LocalReadsSyncStatus; sessionId: number }>
    ) => {
      if (payload.sessionId !== state.sessionId) {
        return;
      }
      state.localReadsSyncStatus = payload.status;
    },
  },
});
